import { useMemo } from "react";
import type { FilterState, DrillLevel } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import { applyBaseFilter, filterRowsByDateRange, filterRowsByScope } from "@/lib/metrics";
import { EMPTY_RANGE, getPriorYearRange } from "@/lib/periods";

export type CancelReasonKey =
  | "no_show"
  | "pending_reschedule"
  | "pet_seen_elsewhere"
  | "concern_resolved"
  | "other"
  | "unknown_reason";

const REASON_COLUMNS: Record<CancelReasonKey, keyof SchedulingRow> = {
  no_show: "canceled_appointments_no_show",
  pending_reschedule: "canceled_appointments_pending_reschedule",
  pet_seen_elsewhere: "canceled_appointments_pet_seen_elsewhere",
  concern_resolved: "canceled_appointments_concern_resolved",
  other: "canceled_appointments_other",
  unknown_reason: "canceled_appointments_unknown_reason",
};

export const REASON_KEYS = Object.keys(REASON_COLUMNS) as CancelReasonKey[];

export interface ReasonMixItem {
  key: CancelReasonKey;
  count: number;
  pct: number; // of total canceled
  pyCount: number | null;
}

export interface CancellationKPIs {
  totalCanceled: number;
  totalBooked: number; // completed + canceled (proxy — true booked count not in dataset)
  cancelRate: number;
  noShowRate: number;
  avgCancelLeadDays: number | null;
  pyTotalCanceled: number | null;
  pyCancelRate: number | null;
  pyNoShowRate: number | null;
  pyAvgCancelLeadDays: number | null;
}

export interface CancellationDrillRow {
  id: string;
  label: string;
  subLabel: string | null;
  booked: number;
  canceled: number;
  cancelRate: number;
  noShows: number;
  noShowRate: number;
  topReason: CancelReasonKey | null;
  pyCancelRate: number | null;
}

interface Totals {
  canceled: number;
  completed: number;
  noShow: number;
  leadDays: number;
  byReason: Record<CancelReasonKey, number>;
}

function emptyTotals(): Totals {
  return {
    canceled: 0,
    completed: 0,
    noShow: 0,
    leadDays: 0,
    byReason: {
      no_show: 0,
      pending_reschedule: 0,
      pet_seen_elsewhere: 0,
      concern_resolved: 0,
      other: 0,
      unknown_reason: 0,
    },
  };
}

function accumulate(t: Totals, r: SchedulingRow): void {
  t.canceled += Number(r.canceled_appointments) || 0;
  t.completed += Number(r.completed_appointments) || 0;
  t.leadDays += Number(r.canceled_appointment_lead_time_days) || 0;
  for (const key of REASON_KEYS) {
    t.byReason[key] += Number(r[REASON_COLUMNS[key]]) || 0;
  }
  t.noShow = t.byReason.no_show;
}

function booked(t: Totals): number {
  return t.canceled + t.completed;
}
function cancelRate(t: Totals): number {
  const b = booked(t);
  return b > 0 ? t.canceled / b : 0;
}
function noShowRate(t: Totals): number {
  const b = booked(t);
  return b > 0 ? t.byReason.no_show / b : 0;
}

function groupKey(row: SchedulingRow, level: DrillLevel): { key: string; label: string } {
  switch (level) {
    case "country":
      return { key: row.country_code, label: row.country_code };
    case "division":
      return { key: row.division_name, label: row.division_name };
    case "group":
      return { key: row.group_name, label: row.group_name };
    case "site":
      return { key: row.location_id, label: `${row.location_id} - ${row.location_name}` };
    case "dvm":
      return { key: row.resource_id, label: row.resource_full_name };
  }
}

export function useCancellationData(
  rows: SchedulingRow[],
  filters: FilterState,
  startDate: string,
  endDate: string,
  drillLevel: DrillLevel
): { kpis: CancellationKPIs; reasonMix: ReasonMixItem[]; drillRows: CancellationDrillRow[] } {
  return useMemo(() => {
    const base = applyBaseFilter(rows, filters.excludeZeroApptDays);
    const scoped = filterRowsByScope(
      filterRowsByDateRange(base, startDate, endDate),
      filters.scope
    );

    const pyRange = getPriorYearRange({ startDate, endDate }, filters.period) ?? EMPTY_RANGE;
    const pyScoped = filterRowsByScope(
      filterRowsByDateRange(base, pyRange.startDate, pyRange.endDate),
      filters.scope
    );
    const hasPy = pyScoped.length > 0;

    const cy = emptyTotals();
    for (const r of scoped) accumulate(cy, r);
    const py = emptyTotals();
    for (const r of pyScoped) accumulate(py, r);

    const kpis: CancellationKPIs = {
      totalCanceled: cy.canceled,
      totalBooked: booked(cy),
      cancelRate: cancelRate(cy),
      noShowRate: noShowRate(cy),
      avgCancelLeadDays: cy.canceled > 0 ? cy.leadDays / cy.canceled : null,
      pyTotalCanceled: hasPy ? py.canceled : null,
      pyCancelRate: hasPy ? cancelRate(py) : null,
      pyNoShowRate: hasPy ? noShowRate(py) : null,
      pyAvgCancelLeadDays: hasPy && py.canceled > 0 ? py.leadDays / py.canceled : null,
    };

    const reasonMix: ReasonMixItem[] = REASON_KEYS.map((key) => ({
      key,
      count: cy.byReason[key],
      pct: cy.canceled > 0 ? cy.byReason[key] / cy.canceled : 0,
      pyCount: hasPy ? py.byReason[key] : null,
    }));

    // Drill rows — group CY and PY by the same entity key
    const groups = new Map<string, { label: string; subSites: Map<string, number>; t: Totals }>();
    for (const r of scoped) {
      const { key, label } = groupKey(r, drillLevel);
      if (!groups.has(key)) groups.set(key, { label, subSites: new Map(), t: emptyTotals() });
      const g = groups.get(key)!;
      accumulate(g.t, r);
      if (drillLevel === "dvm") {
        g.subSites.set(
          r.location_name,
          (g.subSites.get(r.location_name) ?? 0) + (Number(r.canceled_appointments) || 0)
        );
      }
    }
    const pyGroups = new Map<string, Totals>();
    for (const r of pyScoped) {
      const { key } = groupKey(r, drillLevel);
      if (!pyGroups.has(key)) pyGroups.set(key, emptyTotals());
      accumulate(pyGroups.get(key)!, r);
    }

    const drillRows: CancellationDrillRow[] = Array.from(groups.entries())
      .map(([id, g]) => {
        let topReason: CancelReasonKey | null = null;
        let topCount = 0;
        for (const key of REASON_KEYS) {
          if (g.t.byReason[key] > topCount) {
            topCount = g.t.byReason[key];
            topReason = key;
          }
        }
        let subLabel: string | null = null;
        if (drillLevel === "dvm") {
          // Ties broken by name, not by iteration order. A doctor with equal
          // cancellations at two sites -- most often zero at both -- otherwise
          // gets whichever site the rows happened to arrive in first, which is
          // not stable and differed between the two data paths.
          let best = -1;
          for (const [site, count] of g.subSites) {
            if (count > best || (count === best && subLabel !== null && site < subLabel)) {
              best = count;
              subLabel = site;
            }
          }
        }
        const pyT = pyGroups.get(id);
        return {
          id,
          label: g.label,
          subLabel,
          booked: booked(g.t),
          canceled: g.t.canceled,
          cancelRate: cancelRate(g.t),
          noShows: g.t.byReason.no_show,
          noShowRate: noShowRate(g.t),
          topReason,
          pyCancelRate: pyT && booked(pyT) > 0 ? cancelRate(pyT) : null,
        };
      })
      .sort((a, b) => b.cancelRate - a.cancelRate);

    return { kpis, reasonMix, drillRows };
  }, [rows, filters, startDate, endDate, drillLevel]);
}
